import { gql } from 'apollo-angular';
import { USER_FRAGMENT, BONUS_FRAGMENT, LOOTBOX_FRAGMENT } from './fragments.graphql';
import { GraphQLInputTypes, GraphQLTypes } from './types.graphql';

export namespace GraphQLQueries {
  export const KYC_REQUESTS = gql<GraphQLTypes.Result<{ info: GraphQLTypes.PlayerAuth }>, GraphQLTypes.Kyc>`
    query KycRequests($brandId: UUID!, $filter: SiteKycFilterInput!) {
      result: site(brandId: $brandId) {
        info: me {
          player {
            kycRequests(filter: $filter) {
              verificationType
              status
              id
            }
          }
        }
      }
    }
  `;

  export const USER_INFO = gql<GraphQLTypes.Result<{ info: GraphQLTypes.PlayerAuth }>, GraphQLTypes.BrandId>`
    query UserInfo($brandId: UUID!) {
      result: site(brandId: $brandId) {
        info: me {
          loggedIn
          player {
            ...AllUserInfo
          }
        }
      }
    }
    ${USER_FRAGMENT.ALL_USER_INFO}
  `;

  export const SITE_CURRENCY_LIST = gql<GraphQLTypes.Result<{ currencyList: string[] }>, GraphQLTypes.BrandId>`
    query CurrencyList($brandId: UUID!) {
      result: site(brandId: $brandId) {
        currencyList: currencies
      }
    }
  `;

  export const USER_PAYMENT_INFO = gql<GraphQLTypes.Result<{ info: GraphQLTypes.PlayerAuth }>, GraphQLTypes.BrandId>`
    query UserPaymentInfo($brandId: UUID!) {
      result: site(brandId: $brandId) {
        info: me {
          loggedIn
          player {
            ...UserPaymentInfo
          }
        }
      }
    }
    ${USER_FRAGMENT.USER_PAYMENT_INFO}
  `;

  export const PAYMENT_TRANSACTION_LIST = gql<
    GraphQLTypes.Result<{ paymentTransactions: GraphQLTypes.SitePaymentTransactionConnection }>,
    GraphQLInputTypes.PaymentTransactionListInput
  >`
    query PaymentTransactions(
      $brandId: UUID!
      $sitePaymentPaginationInput: SitePaymentPaginationInput
      $sitePaymentTransactionSortInput: SitePaymentTransactionSortInput
      $sitePaymentTransactionFilterInput: SitePaymentTransactionFilterInput
    ) {
      result: site(brandId: $brandId) {
        paymentTransactions(
          sitePaymentPaginationInput: $sitePaymentPaginationInput
          sitePaymentTransactionSortInput: $sitePaymentTransactionSortInput
          sitePaymentTransactionFilterInput: $sitePaymentTransactionFilterInput
        ) {
          edges {
            node {
              amount
              id
              currency
              createdAt
              methodName
              status
              type
              providerName
              cancelable
            }
          }
          totalCount
        }
      }
    }
  `;

  export const WALLET_INFO = gql<GraphQLTypes.Result<{ info: GraphQLTypes.PlayerAuth }>, GraphQLInputTypes.WalletInfoInput>`
    query Wallet($walletId: UUID!, $brandId: UUID!) {
      result: site(brandId: $brandId) {
        info: me {
          player {
            wallet(id: $walletId) {
              balance
              currency
              id
            }
          }
        }
      }
    }
  `;

  export const BET_TRANSACTION_LIST = gql<
    GraphQLTypes.Result<{ gameTransactions: GraphQLTypes.GameTransactionCustomConnection }>,
    GraphQLInputTypes.GameTransactionListInput
  >`
    query gameTransactions(
      $brandId: UUID!
      $paginationInput: PaginationInput
      $siteGameTransactionSortInput: SiteGameTransactionSortInput
    ) {
      result: site(brandId: $brandId) {
        gameTransactions(paginationInput: $paginationInput, siteGameTransactionSortInput: $siteGameTransactionSortInput) {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
          }
          edges {
            node {
              type
              id
              game {
                type
                name
                id
              }
              currency
              createdAt
              amount
            }
          }
        }
      }
    }
  `;

  export const BONUS_LIST = gql<GraphQLTypes.Result<{ info: GraphQLTypes.PlayerAuth }>, GraphQLInputTypes.BonusListInput>`
    query BonusList($brandId: UUID!, $walletsPagination: PaginationInput, $bonusPagination: PaginationInput) {
      result: site(brandId: $brandId) {
        info: me {
          player {
            walletsData: wallets(pagination: $walletsPagination) {
              walletList: edges {
                node {
                  id
                  currency
                  bonusesData: bonuses(pagination: $bonusPagination) {
                    bonuseList: edges {
                      bonus: node {
                        ...Bonus
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    ${BONUS_FRAGMENT.BONUS_ITEM}
  `;

  export const LAST_PLAYED_GAMES = gql<GraphQLTypes.Result<{ gameSessions: GraphQLTypes.SiteGameSessionConnection }>, GraphQLInputTypes.GameSessionInput>`
    query LastPlayedGames($brandId: UUID!, $siteGameSessionFilterInput: SiteGameSessionFilterInput, $siteGameSessionSortInput: SiteGameSessionSortInput, $paginationInput: PaginationInput) {
      result: site(brandId: $brandId) {
        gameSessions(siteGameSessionFilterInput: $siteGameSessionFilterInput, siteGameSessionSortInput: $siteGameSessionSortInput, paginationInput: $paginationInput) {
          edges {
            node {
              id
              game {
                id
                name
                type
              }
              # createdAt
            }
          }
          totalCount
        }
      }
    }
`;

  export const PLAYER_RESTRICTIONS = gql<
    GraphQLTypes.Result<GraphQLTypes.SitePlayerRestriction[]>,
    undefined
  >`
    query SiteActivePlayerRestrictions {
      result: siteActivePlayerRestrictions {
        id
        params {
          name
          value
        }
        restrictionId
        restrictionName
      }
    }
  `;

export const LOOTBOX_LIST = gql<GraphQLTypes.Result<{ info: GraphQLTypes.SiteLootboxConnection }>, GraphQLInputTypes.LootboxListInput>`
  query LootboxList($brandId: UUID!, $paginationInput: PaginationInput) {
    result: site(brandId: $brandId) {
      info: me {
        player {
          lootboxes(paginationInput: $paginationInput) {
            lootboxList: edges {
              lootbox: node {
                ...Lootbox
              }
            }
            totalCount
          }
        }
      }
    }
  }
  ${LOOTBOX_FRAGMENT.LOOTBOX_ITEM}
`;

export const PAYMENT_TRANSACTION_ITEM = gql<GraphQLTypes.Result<{ paymentTransaction: GraphQLTypes.SitePaymentTransaction }>, GraphQLInputTypes.PaymentTransactionItemInput>`
  query PaymentTransaction($paymentTransactionId: UUID!, $brandId: UUID!) {
    result: site(brandId: $brandId) {
      paymentTransaction(id: $paymentTransactionId) {
        id
        amount
        currency
        status
        methodName
        providerName
        createdAt
        paymentInfo
        error {
          description
          code
        }
      }
    }
  }
`;

}
